import Vue from 'vue';
import Vuex from 'vuex';
import createPersistedState from 'vuex-persistedstate';
import Bugsnag from '@bugsnag/js';
import CourseService from '@/services/CourseService';
import { sites } from 'sites-config';

Vue.use(Vuex);

const getDefaultState = () => {
    return {
        site: null,
        token: '',
        user: {},
        language: '',
        dir: '',
        permissions: {
            'browser': false,
            'screens': null,
            'capture': false,
            'camera': false,
            'fullscreen': false
        },
        settings: {
            must_interact: true,
            secure_assessments: true,
            screenshots: false,
            photos: true,
            timeout: 30,
            ai: false
        },
        activeCourse: null,
        activeAssessments: [],
        maintenanceMode: false,
        maintenanceModeMessage: '',
        courseRequestQueue: [],
        syncing: false,
    };
};

export default new Vuex.Store({
    strict: true,
    plugins: [createPersistedState()],
    state: getDefaultState(),
    getters: {
        getToken: state => {
            return state.token;
        },
        getUser: state => {
            return state.user;
        },
        getPermissions: state => {
            return state.permissions;
        },
        getLanguage: state => {
            return state.language;
        },
        getDirection: state => {
            return state.dir;
        },
        getCourse: state => {
            return state.activeCourse;
        },
        getAssessment: state => {
            return state.activeAssessments;
        },
        getIsTrainer: state => {
            if (state.user.type === 'trainer'){
                return true;
            }
            return false;
        },
        getMaintenanceMode: state => {
            return state.maintenanceMode;
        },
        getMaintenanceModeMessage: state => {
            return state.maintenanceModeMessage;
        },
        getCourseRequestQueue: state => {
			return state.courseRequestQueue;
        },
        getSyncing: state => {
            return state.syncing;
        },
        getSecureAssessment: state => {
            return state.settings.secure_assessments;
        },
        getCaptureScreens: state => {
            return state.settings.screenshots;
        },
        getCapturePhotos: state => {
            return state.settings.photos;
        },
        getAI: state => {
            return state.settings.ai;
        },
        getAssessmentAgreement: state => {
            return state.site.assessmentAgreement;
        },
        getMustInteract: state => {
            return state.settings.must_interact;
        },
        getMaxTimeout: state => {
            if (state.settings.timeout < 30){
                return 30 * 1000;
            }
            return state.settings.timeout * 1000;
        },

    },
    mutations: {
        SET_TOKEN: (state, token) => {
            state.token = token;
        },
        SET_USER: (state, user) => {
            state.user = user;
        },
        RESET: (state) => {
            for (const key in state) {
                if (Object.hasOwnProperty.call(state, key)) {
                    if (key !== 'language' && key !== 'dir' && key !== 'site') {
                        state[key] = getDefaultState()[key];
                    }
                }
            }
        },
        RESET_PERMISSIONS: (state) => {
            state.permissions = {
                'browser': false,
                'screens': null,
                'capture': false,
                'camera': false,
                'fullscreen': false
            }
        },
        SET_PERMISSION: (state, [permission, status]) => {
            state.permissions[permission] = status;
        },
        SET_LANGUAGE: (state, [language, direction]) => {
            state.language = language;
            state.dir = direction;
        },
        SET_COURSE: (state, courseID) => {
            state.activeCourse = courseID;
        },
        SET_ASSESSMENT: (state, assessment) => {
            state.activeAssessments.push(assessment);
        },
        SET_RESPONSE: (state, [assesmentID, responses]) => {
            const assessmentIndex = state.activeAssessments.findIndex(a => a.id === assesmentID);
            if (assessmentIndex !== -1) {
                Vue.set(state.activeAssessments[assessmentIndex], 'responses', responses);
            }
        },
        SET_QUESTION: (state, [assessmentID, question]) => {
            const assessmentIndex = state.activeAssessments.findIndex(a => a.id === assessmentID);
            if (assessmentIndex !== -1) {
                Vue.set(state.activeAssessments[assessmentIndex], 'currentQuestion', question);
            }
        },
        CLEAR_ASSESSMENTS: (state) => {
            state.activeAssessments = [];
        },
        CLEAR_COURSE: (state) => {
            state.activeCourse = null;
        },
        CLEAR_ASSESSMENT: (state, assessmentID) => {
            state.activeAssessments = state.activeAssessments.filter(a => a.id !== assessmentID);
        },
        SET_MAINTENANCEMODE: (state, [maintenanceMode, maintenanceModeMessage]) => {
            state.maintenanceMode = maintenanceMode;
            if (maintenanceMode){
                state.maintenanceModeMessage = maintenanceModeMessage;
            } else {
                state.maintenanceModeMessage = '';
            }
        },
        SET_QUEUEITEM: (state, request) => {
            state.courseRequestQueue.push(request);
        },
        DELETE_QUEUEITEM: (state, index) => {
            state.courseRequestQueue.splice(index, 1);
        },
        DELETE_COURSE_QUEUEITEMS: (state, courseID) => {
            state.courseRequestQueue = state.courseRequestQueue.filter(
                request => String(request.course) !== String(courseID)
            );
        },
        DELETE_QUEUE: (state) => {
            state.courseRequestQueue = [];
        },
        SET_SYNC: (state, syncState) => {
            state.syncing = syncState;
        },
        SET_SETTINGS: (state, settings) => {
            if (settings.secure_assessments === '1'){
                settings.secure_assessments = true;
            } else {
                settings.secure_assessments = false;
            }
            if (settings.must_interact === '1'){
                settings.must_interact = true;
            } else {
                settings.must_interact = false;
            }
            state.settings = settings;
        },
        SET_SITE: (state, site) => {
            state.site = site;
        }
    },
    actions: {
        login: ({ commit}, { token, user }) => {
            commit('SET_TOKEN', token);
            commit('SET_USER', user);
            Bugsnag.setUser(user.id, user.email, user.name);
        },
        logout: ({ commit }) => {
            commit('RESET');
        },
        resetPermissions: ({commit}) => {
            commit('RESET_PERMISSIONS');
        },
        permissionChanged: ({ commit }, [permission, status]) => {
            commit('SET_PERMISSION', [permission, status]);
        },
        setLanguage: ({ commit }, [language, direction]) => {
            commit('SET_LANGUAGE', [language, direction]);
        },
        setCourse: ({ commit }, courseID) => {
            commit('SET_COURSE', courseID);
        },
        setAssessment: ({ commit }, assessment) => {
            commit('SET_ASSESSMENT', assessment);
        },
        setResponses: ({ commit }, [assessmentID, responses]) => {
            commit('SET_RESPONSE', [assessmentID, responses]);
        },
        setCurrentQuestion: ({ commit }, [assessmentID, question]) => {
            commit('SET_QUESTION', [assessmentID, question]);
        },
        clearAssessments: ({ commit }) => {
            commit('CLEAR_ASSESSMENTS');
        },
        clearCourse: ({ commit }) => {
            commit('CLEAR_COURSES');
        },
        clearAssessment: ({ commit }, assessmentID) => {
            commit('CLEAR_ASSESSMENT', assessmentID);
        },
        downloadCourse: ({ commit }, courseID) => {
            commit('DOWNLOAD_COURSE', courseID);
        },
        resyncCourses: ({ commit }, courses) => {
            commit('RESYNC_COURSES', courses);
        },
        deleteCourse: ({ commit }, courseID) => {
            commit('DELETE_COURSE', courseID);
        },
        setMaintenanceMode: ({ commit }, [maintenanceMode, maintenanceModeMessage]) => {
            commit('SET_MAINTENANCEMODE', [maintenanceMode, maintenanceModeMessage]);
        },
        addToRequestQueue: ({ commit }, request) => {
            commit('SET_QUEUEITEM', request);
        },
        removeFromRequestQueue: ({ commit }, index) => {
            commit('DELETE_QUEUEITEM', index);
        },
        removeCourseFromRequestQueue: ({ commit }, courseID) => {
            commit('DELETE_COURSE_QUEUEITEMS', courseID);
        },
        emptyRequestQueue: ({ commit }, index) => {
            commit('DELETE_QUEUE', index);
        },
        syncing: ({ getters, commit }) => {
            commit('SET_SYNC', true);
            const queue = getters.getCourseRequestQueue;
            let index = 0;
            for (const queueItem of queue) {
                CourseService[queueItem.type](queueItem.course, queueItem.data ? queueItem.data : null)
                    .then(() => {
                        commit('DELETE_QUEUEITEM', index);
                    });                
                index++;
            }
            commit('SET_SYNC', false);
        },
        setSettings: ({ commit }, settings) => {
            commit('SET_SETTINGS', settings);
        },
        setSite: ({ commit }, site) => {
            let siteFound;
            if (process.env.VUE_APP_STAGE === 'staging') {
                siteFound = Object.values(sites).find(s => s.devUrl === site);
            }
            if (sites[site] && !siteFound){
                siteFound = sites[site];
            } else if (!siteFound) {
                siteFound = sites['app.nucleuslearning.tech'];
            }
            commit('SET_SITE', siteFound);
        }
    }
});
